/**
 * The last resort for a mail the column matchers could not read (Akhil, 2026-09-17: "when the
 * fields are not properly added it falls back saying there is nothing to be done — for those
 * scenarios use DI to get the data and the LLM").
 *
 * `readMailItems` reads what carries its own structure: a labelled column, a numbered line with
 * a unit. A requisition typed as prose, a sheet whose headings it cannot place, a photographed
 * list — all of those used to end at "could not read this mail". This lays out EVERYTHING the
 * mail carries as one document for the LLM reader (`mailBodyExtract.ts`): the body, every
 * sheet's rows, every PDF's text, and an image or a scan as Azure Document Intelligence read it
 * (already turned into sheets or text by the time it reaches here). The model's answer is a
 * suggestion the purchaser reviews, like every LLM read in this app.
 *
 * Bounded: a model's context is finite, and a workbook of thousands of rows is not a requisition
 * anyone reads whole either.
 */

import { htmlToText } from './mail'
import type { ReadableAttachment } from './readMailItems'

export const RESCUE_MAX_CHARS = 60_000

export function rescueText(mail: { subject: string; body_html: string }, attachments: readonly ReadableAttachment[]): string {
  const parts: string[] = []
  const subject = mail.subject.trim()
  if (subject) parts.push(`Subject: ${subject}`)
  const body = htmlToText(mail.body_html ?? '').trim()
  if (body) parts.push(body)

  for (const a of attachments) {
    if (a.kind === 'xlsx' && a.sheets) {
      for (const sheet of a.sheets) {
        const rows = sheet.rows
          .map((row) =>
            Array.from(row, (cell) => (cell === null || cell === undefined ? '' : cell instanceof Date ? cell.toISOString().slice(0, 10) : String(cell).trim()))
              .join(' | ')
              .replace(/(\s\|\s?)+$/, ''),
          )
          .filter((line) => line.replace(/[\s|]/g, '') !== '')
        if (rows.length === 0) continue
        parts.push(`--- file: ${a.name} · sheet: ${sheet.name} ---`, ...rows)
      }
    } else if (typeof a.text === 'string' && a.text.trim() !== '') {
      parts.push(`--- file: ${a.name} ---`, a.text.trim())
    }
  }

  const text = parts.join('\n')
  return text.length > RESCUE_MAX_CHARS ? `${text.slice(0, RESCUE_MAX_CHARS - 1)}…` : text
}
